import { ImageUp, Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { api, errorMessage, rawFetch, unwrap, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { isSettingsConflict, useBrandDefaultTexts, useEffectiveSettings, useOrganization, useSaveSettings } from "../api/queries";
import { HomeownerLanguageField } from "../i18n/HomeownerLanguage";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { CalculatorPreview } from "../components/CalculatorPreview";
import { OwnTextField, OwnTextHead } from "../components/OwnText";
import { Alert, Button, Card, ChipsInput, Field, NumberInput, PageHead, Select, Spinner, Switch, TextInput, cx, useToast } from "../components/ui";
import { contrast, isHex } from "../lib/color";
import { sameDocument } from "../lib/sameDocument";
import { isWebLink, websiteLink } from "../lib/website";
import { useDebounced } from "./admin/Orgs";
import { useLeaveGuard } from "../lib/leaveGuard";
import { isOwnText, ownTextError } from "../lib/ownText";

type Brand = S["BrandSettings"];
type ColorKey = keyof Brand["colors"];
type AfterQuote = Brand["after_quote"];
type QuoteNumbers = S["QuoteNumberFormat"];
type ProposalTexts = NonNullable<Brand["proposal"]>;
type ProposalStep = S["ProposalStep"];
type DefaultTexts = S["BrandDefaultTexts"];

/** The proposal's texts: each ours (translated) unless the company writes its own. "What happens next" is one switch
 * for all its steps: ours are the five default steps, in each homeowner's language. */
function ProposalSection({ value, ours, error, disabled, onChange }: {
  value: ProposalTexts;
  ours: DefaultTexts | undefined;
  error: string | null;
  disabled?: boolean;
  onChange: (v: ProposalTexts) => void;
}) {
  const steps = value.timeline ?? [];
  const setStep = (i: number, patch: Partial<ProposalStep>) => onChange({ ...value, timeline: steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const t = useT();
  const stepsId = useId();
  const ownSteps = isOwnText(steps);
  const withoutSteps = () => {
    const rest = { ...value };
    delete rest.timeline;
    onChange(rest);
  };
  return (
    <Card title={t("branding.proposal.title")} description={t("branding.proposal.description")}>
      <div className="stack">
        <OwnTextField
          label={t("branding.proposal.about.label")}
          multiline
          maxLength={1500}
          value={value.about}
          ours={ours?.about}
          disabled={disabled}
          ownHint={`${t("common.ownText.own")} ${t("branding.proposal.about.tip")}`}
          onChange={(about) => onChange({ ...value, about })}
        />
        <div className="field own-text" data-own-text={ownSteps ? "own" : "ours"}>
          <OwnTextHead
            id={stepsId}
            label={t("branding.proposal.steps.label")}
            own={ownSteps}
            disabled={disabled}
            onOn={() => onChange({ ...value, timeline: (ours?.timeline ?? [{ title: "", duration: null, description: null }]).map((s) => ({ ...s })) })}
            onOff={withoutSteps}
          />
          {!ownSteps ? (
            <ol className="own-steps-ours" id={`${stepsId}-text`} aria-labelledby={`${stepsId}-label`} dir="auto">
              {(ours?.timeline ?? []).map((s, i) => (
                <li key={i}>
                  <b>{s.title}</b>
                  {s.duration ? ` · ${s.duration}` : ""}
                </li>
              ))}
            </ol>
          ) : (
          <div className="stack">
            {steps.map((s, i) => (
              <div className="proposal-step" key={i}>
                <TextInput dir="auto" className="input step-title" aria-label={t("branding.proposal.step.title")} maxLength={60} placeholder={t("branding.proposal.step.title")} value={s.title} onChange={(e) => setStep(i, { title: e.target.value })} />
                <TextInput dir="auto" className="input step-duration" aria-label={t("branding.proposal.step.duration")} maxLength={40} placeholder={t("branding.proposal.step.durationExample")} value={s.duration ?? ""} onChange={(e) => setStep(i, { duration: e.target.value || null })} />
                <Button variant="ghost" icon={<Trash2 />} aria-label={t("branding.proposal.step.remove")} title={t("branding.proposal.step.remove")} onClick={() => onChange({ ...value, timeline: steps.filter((_, j) => j !== i) })} />
                <TextInput dir="auto" className="input step-description" aria-label={t("branding.proposal.step.description")} maxLength={240} placeholder={t("branding.proposal.step.descriptionPlaceholder")} value={s.description ?? ""} onChange={(e) => setStep(i, { description: e.target.value || null })} />
              </div>
            ))}
            <div>
              <Button icon={<Plus />} disabled={disabled || steps.length >= 8} onClick={() => onChange({ ...value, timeline: [...steps, { title: "", duration: null, description: null }] })}>{t("branding.proposal.steps.add")}</Button>
            </div>
          </div>
          )}
          {error ? (
            <span className="field-error">{error}</span>
          ) : (
            <span className="hint">{ownSteps ? `${t("common.ownText.own")} ${t("branding.proposal.steps.hint", { max: 8 })}` : t("common.ownText.ours")}</span>
          )}
        </div>
        <OwnTextField
          label={t("branding.proposal.terms.label")}
          multiline
          maxLength={2000}
          value={value.terms}
          ours={ours?.terms}
          disabled={disabled}
          oursHint={t("branding.proposal.terms.ours")}
          onChange={(terms) => onChange({ ...value, terms })}
        />
      </div>
    </Card>
  );
}

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const SIGNER_EMAIL = /^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/;

type CompanySigner = S["CompanySigner"];
type SignerErrors = Partial<Record<"email" | "name" | "title", MessageKey>>;

/** While Company signature is on, the signer's email, name and title are required (as the server checks). */
export function companySignerErrors(signer: CompanySigner | null | undefined): SignerErrors {
  if (!signer?.enabled) return {};
  const errors: SignerErrors = {};
  if (!SIGNER_EMAIL.test((signer.email ?? "").trim())) errors.email = "branding.companySigner.emailError";
  if (!(signer.name ?? "").trim()) errors.name = "branding.companySigner.nameError";
  if (!(signer.title ?? "").trim()) errors.title = "branding.companySigner.jobTitleError";
  return errors;
}

/** Branding › Proposal PDF › Company signature: off by default (the homeowner alone signs, and the proposal prints no
 * company block); on, the acceptance page shows the company's block and the signer gets the signing email after the
 * homeowner. */
function CompanySignatureCard({ value, disabled, showErrors, onChange }: {
  value: CompanySigner | null | undefined;
  disabled?: boolean;
  showErrors: boolean;
  onChange: (v: CompanySigner) => void;
}) {
  const t = useT();
  const signer: CompanySigner = value ?? { enabled: false };
  const errors = companySignerErrors(signer);
  const set = (patch: Partial<CompanySigner>) => onChange({ ...signer, ...patch });
  const error = (k: keyof SignerErrors) => (showErrors && errors[k] ? t(errors[k]!) : undefined);
  return (
    <Card title={t("branding.companySigner.title")} description={t("branding.companySigner.description")}>
      <div className="stack">
        <div className="switch-row">
          <div>
            <b>{t("branding.companySigner.switch")}</b>
            <span className="hint">{t(signer.enabled ? "branding.companySigner.onHint" : "branding.companySigner.offHint")}</span>
          </div>
          <Switch checked={!!signer.enabled} disabled={disabled} onChange={(v) => set({ enabled: v })} />
        </div>
        {signer.enabled && (
          <div className="grid-2">
            <Field label={t("branding.companySigner.name")} error={error("name")}>
              <TextInput dir="auto" maxLength={120} autoComplete="name" disabled={disabled} value={signer.name ?? ""} onChange={(e) => set({ name: e.target.value })} />
            </Field>
            <Field label={t("branding.companySigner.jobTitle")} hint={t("branding.companySigner.jobTitleHint")} error={error("title")}>
              <TextInput dir="auto" maxLength={120} autoComplete="organization-title" disabled={disabled} value={signer.title ?? ""} onChange={(e) => set({ title: e.target.value })} />
            </Field>
            <Field label={t("branding.companySigner.email")} hint={t("branding.companySigner.emailHint")} error={error("email")}>
              <TextInput type="email" dir="ltr" maxLength={254} autoComplete="email" disabled={disabled} value={signer.email ?? ""} onChange={(e) => set({ email: e.target.value })} />
            </Field>
          </div>
        )}
      </div>
    </Card>
  );
}

type BrandTab = "basics" | "quote" | "proposal";
/** The page's sections: what the quote and the proposal share, the quote, and the proposal PDF. */
export const BRAND_TABS: { id: BrandTab; label: MessageKey; note: MessageKey; help: MessageKey }[] = [
  { id: "basics", label: "branding.tabs.basics.label", note: "branding.tabs.basics.note", help: "branding.tabs.basics.help" },
  { id: "quote", label: "branding.tabs.quote.label", note: "branding.tabs.quote.note", help: "branding.tabs.quote.help" },
  { id: "proposal", label: "branding.tabs.proposal.label", note: "branding.tabs.proposal.note", help: "branding.tabs.proposal.help" },
];

/** The scheduling service a booking link points to, and whether the calculator can show it inside the page. */
export function bookingProvider(url: string | null | undefined): { label: MessageKey; inline: boolean } | null {
  if (!url) return null;
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (/(^|\.)calendly\.com$/.test(host)) return { label: "branding.booking.provider.calendly", inline: true };
  if (/(^|\.)hubspot\.com$/.test(host)) return { label: "branding.booking.provider.hubspot", inline: true };
  if (host === "calendar.google.com" || host === "calendar.app.google") return { label: "branding.booking.provider.google", inline: true };
  if (/(^|\.)(office365\.com|office\.com|outlook\.com|microsoft\.com)$/.test(host)) return { label: "branding.booking.provider.microsoft", inline: false };
  return { label: "branding.booking.provider.other", inline: false };
}

/** Font names are names (never translated), except the two descriptive entries (`key`). */
const FONTS: { value: string; label: string; key?: MessageKey }[] = [
  { value: "Inter, system-ui, sans-serif", label: "Inter" },
  { value: "'DM Sans', system-ui, sans-serif", label: "DM Sans" },
  { value: "'Poppins', system-ui, sans-serif", label: "Poppins" },
  { value: "'Montserrat', system-ui, sans-serif", label: "Montserrat" },
  { value: "'Open Sans', system-ui, sans-serif", label: "Open Sans" },
  { value: "'Lato', system-ui, sans-serif", label: "Lato" },
  { value: "'Nunito', system-ui, sans-serif", label: "Nunito" },
  { value: "Georgia, 'Times New Roman', serif", label: "Georgia", key: "branding.identity.font.georgia" },
  { value: "system-ui, -apple-system, 'Segoe UI', sans-serif", label: "system-ui", key: "branding.identity.font.system" },
];

const COLOR_FIELDS: { key: ColorKey; label: MessageKey; hint: MessageKey }[] = [
  { key: "primary", label: "branding.colours.primary.label", hint: "branding.colours.primary.hint" },
  { key: "secondary", label: "branding.colours.secondary.label", hint: "branding.colours.secondary.hint" },
  { key: "accent", label: "branding.colours.accent.label", hint: "branding.colours.accent.hint" },
  { key: "background", label: "branding.colours.background.label", hint: "branding.colours.background.hint" },
  { key: "text", label: "branding.colours.text.label", hint: "branding.colours.text.hint" },
];

const OPTIONAL_STEPS: { step: S["FlowStep"]; label: MessageKey }[] = [
  { step: "roof_pin", label: "branding.flow.step.roofPin" },
  { step: "usage", label: "branding.flow.step.usage" },
  { step: "roof_type", label: "branding.flow.step.roofType" },
  { step: "contact", label: "branding.flow.step.contact" },
];
const STEP_ORDER: S["FlowStep"][] = ["address", "roof_pin", "usage", "roof_type", "contact", "proposal"];

function ColorField({ label, hint, value, onChange }: { label: string; hint: string; value: string; onChange: (v: string) => void }) {
  const [text, setText] = useState(value);
  const t = useT();
  useEffect(() => setText(value), [value]);
  return (
    <Field label={label} hint={hint} error={!isHex(text) ? t("branding.colours.hexError", { example: "#3b5bff" }) : undefined}>
      <div className="swatch-row">
        <label className="swatch" style={{ background: isHex(value) ? value : "#fff" }} title={t("branding.colours.pick")}>
          <input type="color" value={value.length === 4 ? `#${value.slice(1).split("").map((c) => c + c).join("")}` : value} onChange={(e) => onChange(e.target.value)} />
        </label>
        <input
          className="input mono"
          dir="ltr"
          aria-label={label}
          value={text}
          maxLength={7}
          onChange={(e) => {
            const v = e.target.value.startsWith("#") ? e.target.value : `#${e.target.value}`;
            setText(v);
            if (isHex(v)) onChange(v);
          }}
        />
      </div>
    </Field>
  );
}

const PREFIX = /^[A-Za-z0-9_-]{0,12}$/;

/** What is wrong with a quote-number format (its message key; null when it is valid). */
export function quoteNumbersError(f: QuoteNumbers): MessageKey | null {
  if (!PREFIX.test(f.prefix)) return "branding.quoteNumbers.error.prefix";
  if (!Number.isInteger(f.start_at) || f.start_at < 0 || f.start_at > 999999999) return "branding.quoteNumbers.error.startAt";
  if (!Number.isInteger(f.step) || f.step < 1 || f.step > 1000) return "branding.quoteNumbers.error.increment";
  if (!Number.isInteger(f.padding) || f.padding < 1 || f.padding > 10) return "branding.quoteNumbers.error.digits";
  return null;
}

/** "After the quote › Quote numbers": the format and a live preview of the next 3 numbers (from the server, which
 * knows the numbers already issued). Changing it never renumbers existing quotes. */
function QuoteNumbersSection({ orgId, value, onChange }: { orgId: string; value: QuoteNumbers; onChange: (v: QuoteNumbers) => void }) {
  const set = (patch: Partial<QuoteNumbers>) => onChange({ ...value, ...patch });
  const t = useT();
  const intl = useIntlShape();
  const error = quoteNumbersError(value);
  const format = useDebounced(value, 250);
  const preview = useQuery({
    queryKey: ["quote-numbers-preview", orgId, format],
    enabled: !quoteNumbersError(format),
    queryFn: () => unwrap(api.POST("/v1/orgs/{org_id}/quote-numbers/preview", { params: { path: { org_id: orgId } }, body: format })),
    placeholderData: (previous) => previous,
  });
  return (
    <div className="stack">
      <div>
        <b>{t("branding.quoteNumbers.title")}</b>
        <span className="hint" style={{ display: "block" }}>
          {t("branding.quoteNumbers.hint")}
        </span>
      </div>
      <div className="form-grid">
        <Field label={t("branding.quoteNumbers.prefix.label")} hint={t("branding.quoteNumbers.prefix.hint")}>
          <TextInput className="input mono" dir="ltr" maxLength={12} value={value.prefix} onChange={(e) => set({ prefix: e.target.value })} />
        </Field>
        <Field label={t("branding.quoteNumbers.startAt")}>
          <NumberInput value={value.start_at} min={0} step={1} onChange={(v) => set({ start_at: Math.max(0, Math.round(v ?? 1)) })} />
        </Field>
        <Field label={t("branding.quoteNumbers.increment")}>
          <NumberInput value={value.step} min={1} max={1000} step={1} onChange={(v) => set({ step: Math.round(v ?? 1) })} />
        </Field>
        <Field label={t("branding.quoteNumbers.digits.label")} hint={t("branding.quoteNumbers.digits.hint")}>
          <NumberInput value={value.padding} min={1} max={10} step={1} onChange={(v) => set({ padding: Math.round(v ?? 6) })} />
        </Field>
      </div>
      <div className="switch-row">
        <div>
          <b>{t("branding.quoteNumbers.includeYear.title")}</b>
          <span className="hint">
            {intl.formatMessage(
              { id: "branding.quoteNumbers.includeYear.example" },
              { withYear: <bdi className="i18n-ltr">{value.prefix}2026-000123</bdi>, without: <bdi className="i18n-ltr">{value.prefix}000123</bdi> },
            )}
          </span>
        </div>
        <Switch checked={value.include_year} onChange={(v) => set({ include_year: v, reset_yearly: v ? value.reset_yearly : false })} />
      </div>
      <div className="switch-row">
        <div>
          <b>{t("branding.quoteNumbers.resetYearly.title")}</b>
          <span className="hint">{value.include_year ? t("branding.quoteNumbers.resetYearly.hint", { start: String(value.start_at) }) : t("branding.quoteNumbers.resetYearly.needsYear")}</span>
        </div>
        <Switch checked={value.include_year && value.reset_yearly} disabled={!value.include_year} onChange={(v) => set({ reset_yearly: v })} />
      </div>
      {error ? (
        <Alert tone="danger">{t(error)}</Alert>
      ) : (
        <div className="quote-number-preview" id="quoteNumberPreview">
          <span className="hint">{t("branding.quoteNumbers.next")}</span>
          {(preview.data?.numbers ?? []).map((n) => (
            <code key={n} className="mono" dir="ltr">{n}</code>
          ))}
          {preview.isError && <span className="hint">{errorMessage(preview.error)}</span>}
        </div>
      )}
    </div>
  );
}

/** Colour pairs that read badly: the message key and the contrast ratio (and the ratio to aim for). */
function contrastWarnings(c: Brand["colors"]): { key: MessageKey; ratio: number; target: number }[] {
  const out: { key: MessageKey; ratio: number; target: number }[] = [];
  const check = (key: MessageKey, fg: string, min: number) => {
    const ratio = contrast(fg, c.background);
    if (ratio < min) out.push({ key, ratio, target: min });
  };
  check("branding.colours.warn.text", c.text, 4.5);
  check("branding.colours.warn.secondary", c.secondary, 3);
  check("branding.colours.warn.primary", c.primary, 2);
  check("branding.colours.warn.accent", c.accent, 2);
  return out;
}

/** The optional status mark on the quote page: "Charter Installer on Solar OS" while the company holds the badge
 * (an approved switch); off by default. */
function CharterMarkCard({ checked, onChange }: { checked: boolean; onChange: (value: boolean) => void }) {
  const orgId = useOrgId();
  const company = useOrganization(orgId);
  const badge = company.data?.badge;
  const t = useT();
  return (
    <Card title={t("branding.charter.title")} description={t("branding.charter.description")}>
      <div className="switch-row">
        <div>
          <b>{badge ? t("branding.charter.mark", { badge: badge.name }) : t("branding.charter.title")}</b>
          <span className="hint">
            {badge ? t("branding.charter.hint") : t("branding.charter.unavailable")}
          </span>
        </div>
        <Switch checked={checked && !!badge} disabled={!badge} onChange={onChange} />
      </div>
    </Card>
  );
}

export function Branding() {
  const orgId = useOrgId();
  const { can } = useOrg();
  const canManage = can("branding", "edit");
  const settings = useEffectiveSettings(orgId);
  const save = useSaveSettings(orgId);
  const toast = useToast();
  const t = useT();
  const intl = useIntlShape();
  const ours = useBrandDefaultTexts(orgId).data;
  const [brand, setBrand] = useState<Brand | null>(null);
  // the branding this page's edits start from, and its version: a save names it, and the API refuses one made after
  // someone else saved (two tabs, two teammates), instead of putting their older values back without a word
  const [loaded, setLoaded] = useState<{ brand: Brand; version: number } | null>(null);
  const [conflict, setConflict] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [tab, setTab] = useState<BrandTab>("basics");
  const fileRef = useRef<HTMLInputElement>(null);
  const saved = loaded?.brand;

  const start = (data: S["EffectiveSettings"]) => {
    setLoaded({ brand: structuredClone(data.documents.brand), version: data.versions.brand ?? 0 });
    setBrand(structuredClone(data.documents.brand));
    setConflict(false);
  };
  useEffect(() => {
    if (settings.data && !brand) start(settings.data);
  }, [settings.data, brand]);
  const dirty = useMemo(() => !!brand && !sameDocument(brand, saved), [brand, saved]);
  // a reload or closing the tab with unsaved changes asks first (live test EX-05: they were lost without a word)
  useLeaveGuard(dirty);
  const warnings = brand ? contrastWarnings(brand.colors) : [];

  if (settings.isLoading || !brand) return settings.error ? <Alert tone="danger">{errorMessage(settings.error)}</Alert> : <Spinner />;

  const set = (patch: Partial<Brand>) => setBrand({ ...brand, ...patch });
  const setColor = (key: ColorKey, value: string) => set({ colors: { ...brand.colors, [key]: value } });
  const setText = (key: keyof Brand["texts"], value: string | null) => set({ texts: { ...brand.texts, [key]: value } });
  const company = (text: string) => text.split("{company}").join(brand.company_name);
  const setContact = (key: keyof Brand["contact"], value: string) => set({ contact: { ...brand.contact, [key]: value || null } });
  // a website typed as "www.sunny.com" becomes "https://www.sunny.com"; anything else that is not a web link (a
  // "javascript:" link) is refused here and by the API, since homeowners click it in the calculator and the emails
  const contactUrlError = brand.contact.url && !isWebLink(brand.contact.url) ? t("branding.after.booking.error") : undefined;
  const contactEmailError = brand.contact.email && !EMAIL.test(brand.contact.email) ? t("branding.after.leadEmails.invalid") : undefined;
  const after: AfterQuote = brand.after_quote;
  const setAfter = (patch: Partial<AfterQuote>) => set({ after_quote: { ...after, ...patch } });
  const booking = bookingProvider(after.booking_url);
  const bookingError = after.booking_url && !/^https:\/\/\S+$/.test(after.booking_url) ? t("branding.after.booking.error") : undefined;
  const validityError = !Number.isInteger(after.validity_days) || after.validity_days < 1 || after.validity_days > 365 ? t("branding.after.validity.error", { min: 1, max: 365 }) : undefined;
  const numbersError = quoteNumbersError(after.quote_numbers);
  const toggleStep = (step: S["FlowStep"], on: boolean) => {
    const steps = new Set(brand.flow.steps);
    if (on) steps.add(step);
    else steps.delete(step);
    set({ flow: { ...brand.flow, steps: STEP_ORDER.filter((s) => steps.has(s)) } });
  };

  async function upload(file: File) {
    if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
      toast(t("branding.logo.badType"), true);
      return;
    }
    if (file.size > 1024 * 1024) {
      toast(t("branding.logo.tooBig"), true);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await rawFetch(`/api/v1/orgs/${orgId}/brand/logo`, { method: "POST", body: form });
      const logo = (await res.json()) as S["BrandLogo"];
      set({ logo_url: logo.url });
      toast(t("branding.logo.uploaded"));
    } catch (e) {
      toast(errorMessage(e), true);
    } finally {
      setUploading(false);
    }
  }

  const stepsError = (brand.proposal?.timeline ?? []).some((s) => !s.title.trim()) ? t("branding.proposal.steps.error") : null;

  const colourError = COLOR_FIELDS.some((f) => !isHex(brand.colors[f.key])) || !brand.company_name.trim();
  // an own text left empty says nothing: write it, or go back to ours
  const ownEmpty = (value: string | null | undefined, allowEmpty = false) => !!ownTextError(value, allowEmpty);
  const tabErrors: Record<BrandTab, boolean> = {
    basics: colourError || !!contactUrlError || !!contactEmailError,
    quote: !!bookingError || !!validityError || !!numbersError || ownEmpty(brand.texts.headline) || ownEmpty(brand.texts.call_to_action)
      || ownEmpty(after.button_label) || ownEmpty(after.next_steps),
    proposal: !!stepsError || ownEmpty(brand.proposal?.about) || ownEmpty(brand.proposal?.terms)
      || Object.keys(companySignerErrors(brand.proposal?.company_signer)).length > 0,
  };

  const reload = async () => {
    const fresh = (await settings.refetch()).data;
    if (fresh) start(fresh);
  };

  const saveBar = (
    <div className="row">
      <Button variant="ghost" icon={<RotateCcw />} disabled={!dirty || save.isPending} onClick={() => setBrand(structuredClone(saved!))}>
        {t("branding.save.discard")}
      </Button>
      <Button
        variant="primary"
        icon={<Save />}
        disabled={!dirty || !canManage || conflict || Object.values(tabErrors).some(Boolean)}
        loading={save.isPending}
        onClick={() =>
          save.mutate(
            { kind: "brand", document: brand, baseVersion: loaded?.version },
            {
              onSuccess: async () => {
                toast(t("branding.save.done"));
                // edit on from the document as stored (the API leaves out empty fields), and its new version
                const fresh = (await settings.refetch()).data;
                if (fresh) start(fresh);
              },
              // someone else saved since this page loaded: keep this page's edits on screen until the user reloads
              onError: (e) => (isSettingsConflict(e) ? setConflict(true) : toast(errorMessage(e), true)),
            },
          )
        }
      >
        {t("branding.save.save")}
      </Button>
    </div>
  );

  return (
    <>
      <PageHead title={t("branding.page.title")} description={t("branding.page.description")} actions={saveBar} />
      {conflict && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="warn" title={t("branding.conflict.title")} action={<Button icon={<RotateCcw />} onClick={() => void reload()}>{t("branding.conflict.reload")}</Button>}>
            {t("branding.conflict.hint")}
          </Alert>
        </div>
      )}
      {!canManage && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="info">{t("branding.page.readOnly")}</Alert>
        </div>
      )}
      <div className="tabs brand-tabs" role="tablist" aria-label={t("branding.tabs.aria")}>
        {BRAND_TABS.map((b) => (
          <button key={b.id} className={cx(tab === b.id && "on")} onClick={() => setTab(b.id)} role="tab" aria-selected={tab === b.id} id={`brand-tab-${b.id}`}>
            <span className="brand-tab-label">
              {t(b.label)}
              {tabErrors[b.id] && (
                <span className="tab-count" title={t("branding.tabs.fixField")} style={{ marginInlineStart: 6 }}>
                  !
                </span>
              )}
            </span>{" "}
            <span className="brand-tab-note">{t("branding.tabs.note", { note: t(b.note) })}</span>
          </button>
        ))}
      </div>
      <p className="hint brand-tab-help" role="note">{t(BRAND_TABS.find((b) => b.id === tab)!.help)}</p>
      <div className="split brand-split" role="tabpanel" aria-labelledby={`brand-tab-${tab}`}>
        <div className="stack">
          {tab === "basics" && (
            <>
              <Card title={t("branding.identity.title")} description={t("branding.identity.description")}>
                <div className="stack">
                  <Field label={t("branding.identity.logo.label")} hint={t("branding.identity.logo.hint")}>
                    <div
                      className={cx("logo-drop", drag && "drag")}
                      onDragOver={(e) => {
                        e.preventDefault();
                        setDrag(true);
                      }}
                      onDragLeave={() => setDrag(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDrag(false);
                        const f = e.dataTransfer.files[0];
                        if (f) void upload(f);
                      }}
                    >
                      <div className="logo-box">{brand.logo_url ? <img src={brand.logo_url} alt={t("branding.identity.logo.alt")} /> : <ImageUp color="var(--faint)" />}</div>
                      <div className="stack" style={{ gap: 8 }}>
                        <span className="small muted">{t("branding.identity.logo.drop")}</span>
                        <div className="row">
                          <Button size="sm" icon={<ImageUp />} loading={uploading} disabled={!canManage} onClick={() => fileRef.current?.click()}>
                            {t("branding.identity.logo.upload")}
                          </Button>
                          {brand.logo_url && (
                            <Button size="sm" variant="ghost" icon={<Trash2 />} disabled={!canManage} onClick={() => set({ logo_url: null })}>
                              {t("branding.action.remove")}
                            </Button>
                          )}
                        </div>
                      </div>
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        hidden
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void upload(f);
                          e.target.value = "";
                        }}
                      />
                    </div>
                  </Field>
                  <div className="form-grid">
                    <Field
                      label={t("branding.identity.companyName.label")}
                      hint={t("branding.identity.companyName.hint")}
                      error={!brand.company_name.trim() ? t("branding.identity.companyName.required") : undefined}
                    >
                      <TextInput dir="auto" value={brand.company_name} maxLength={200} onChange={(e) => set({ company_name: e.target.value })} />
                    </Field>
                    <Field label={t("branding.identity.font.label")} hint={t("branding.identity.font.hint")}>
                      <Select
                        value={FONTS.some((f) => f.value === brand.font_family) ? brand.font_family : "__custom"}
                        onChange={(v) => v !== "__custom" && set({ font_family: v })}
                        options={[
                          ...FONTS.map((f) => ({ value: f.value, label: f.key ? t(f.key, { name: f.label }) : f.label })),
                          ...(FONTS.some((f) => f.value === brand.font_family) ? [] : [{ value: "__custom", label: brand.font_family }]),
                        ]}
                      />
                    </Field>
                  </div>
                </div>
              </Card>
              <Card title={t("branding.colours.title")} description={t("branding.colours.description")}>
                <div className="stack">
                  <div className="form-grid">
                    {COLOR_FIELDS.map((f) => (
                      <ColorField key={f.key} label={t(f.label)} hint={t(f.hint)} value={brand.colors[f.key]} onChange={(v) => setColor(f.key, v)} />
                    ))}
                  </div>
                  {warnings.length > 0 && (
                    <Alert tone="warn" title={t("branding.colours.contrastTitle")}>
                      <ul style={{ margin: "4px 0 0", paddingInlineStart: 18 }}>
                        {warnings.map((w) => (
                          <li key={w.key}>
                            {t(w.key, {
                              ratio: intl.formatNumber(w.ratio, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
                              target: intl.formatNumber(w.target, { maximumFractionDigits: 1 }),
                            })}
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                </div>
              </Card>
              <Card title={t("branding.contact.title")} description={t("branding.contact.description")}>
                <div className="form-grid">
                  <Field label={t("branding.contact.email")} error={contactEmailError}>
                    <TextInput type="email" dir="ltr" value={brand.contact.email ?? ""} onChange={(e) => setContact("email", e.target.value.trim())} />
                  </Field>
                  <Field label={t("branding.contact.phone")}>
                    <TextInput type="tel" dir="ltr" value={brand.contact.phone ?? ""} onChange={(e) => setContact("phone", e.target.value)} />
                  </Field>
                  <Field label={t("branding.contact.website")} className="span-2" error={contactUrlError}>
                    {/* eslint-disable-next-line solaros-i18n/no-hardcoded-text -- a URL scheme, not words */}
                    <TextInput type="url" dir="ltr" value={brand.contact.url ?? ""} onChange={(e) => setContact("url", e.target.value.trim())} onBlur={(e) => setContact("url", websiteLink(e.target.value))} placeholder="https://" />
                  </Field>
                  <HomeownerLanguageField orgId={orgId} disabled={!canManage} />
                </div>
              </Card>
            </>
          )}
          {tab === "quote" && (
            <>
              <Card title={t("branding.texts.title")} description={t("branding.texts.description")}>
                <div className="stack">
                  <OwnTextField label={t("branding.texts.headline")} value={brand.texts.headline} ours={ours?.headline} maxLength={200} disabled={!canManage} onChange={(v) => setText("headline", v)} />
                  <OwnTextField
                    label={t("branding.texts.subheadline")}
                    value={brand.texts.subheadline}
                    ours={ours?.subheadline}
                    maxLength={500}
                    allowEmpty
                    disabled={!canManage}
                    ownHint={t("branding.texts.subheadlineOwn")}
                    onChange={(v) => setText("subheadline", v)}
                  />
                  <OwnTextField
                    label={t("branding.texts.callToAction.label")}
                    value={brand.texts.call_to_action}
                    ours={ours?.call_to_action}
                    maxLength={100}
                    disabled={!canManage}
                    oursHint={`${t("common.ownText.ours")} ${t("branding.texts.callToAction.hint")}`}
                    ownHint={`${t("common.ownText.own")} ${t("branding.texts.callToAction.hint")}`}
                    onChange={(v) => setText("call_to_action", v)}
                  />
                </div>
              </Card>
              <Card title={t("branding.after.title")} description={t("branding.after.description")}>
                <div className="stack">
                  <Field
                    label={t("branding.after.booking.label")}
                    error={bookingError}
                    hint={
                      booking
                        ? t("branding.after.booking.found", { provider: t(booking.label), inline: booking.inline ? "yes" : "no" })
                        : t("branding.after.booking.hint")
                    }
                  >
                    <TextInput type="url" dir="ltr" placeholder={t("branding.after.booking.placeholder")} value={after.booking_url ?? ""} onChange={(e) => setAfter({ booking_url: e.target.value.trim() || null })} />
                  </Field>
                  <div className="form-grid">
                    <OwnTextField
                      label={t("branding.after.button.label")}
                      value={after.button_label}
                      ours={after.booking_url ? ours?.button_label_booking : ours?.button_label_interest}
                      show={company}
                      maxLength={80}
                      disabled={!canManage}
                      onChange={(v) => setAfter({ button_label: v })}
                    />
                    <Field label={t("branding.after.validity.label")} error={validityError}>
                      <NumberInput value={after.validity_days} min={1} max={365} step={1} suffix={t("branding.after.validity.suffix")} onChange={(v) => setAfter({ validity_days: v ?? 30 })} />
                    </Field>
                  </div>
                  <OwnTextField
                    label={t("branding.after.nextSteps.label")}
                    value={after.next_steps}
                    ours={ours?.next_steps}
                    show={company}
                    maxLength={300}
                    disabled={!canManage}
                    ownHint={`${t("common.ownText.own")} ${t("branding.after.nextSteps.hint", { token: "{company}" })}`}
                    onChange={(v) => setAfter({ next_steps: v })}
                  />
                  <Field label={t("branding.after.leadEmails.label")} hint={t("branding.after.leadEmails.hint")}>
                    <ChipsInput
                      values={after.lead_emails}
                      onChange={(v) => setAfter({ lead_emails: v.slice(0, 10) })}
                      placeholder={t("branding.after.leadEmails.placeholder")}
                      normalize={(v) => v.trim().toLowerCase()}
                      validate={(v) => (EMAIL.test(v) ? null : t("branding.after.leadEmails.invalid"))}
                    />
                  </Field>
                  <div className="switch-row">
                    <div>
                      <b>{t("branding.after.emailHomeowner.title")}</b>
                      <span className="hint">{t("branding.after.emailHomeowner.hint")}</span>
                    </div>
                    <Switch checked={after.email_homeowner_copy} onChange={(v) => setAfter({ email_homeowner_copy: v })} />
                  </div>
                  <QuoteNumbersSection orgId={orgId} value={after.quote_numbers} onChange={(v) => setAfter({ quote_numbers: v })} />
                </div>
              </Card>
              <Card title={t("branding.flow.title")} description={t("branding.flow.description")}>
                <div>
                  <div className="switch-row">
                    <div>
                      <b>{t("branding.flow.battery.title")}</b>
                      <span className="hint">{t("branding.flow.battery.hint")}</span>
                    </div>
                    <Switch checked={brand.flow.show_battery} onChange={(v) => set({ flow: { ...brand.flow, show_battery: v } })} />
                  </div>
                  <div className="switch-row">
                    <div>
                      <b>{t("branding.flow.financing.title")}</b>
                      <span className="hint">{t("branding.flow.financing.hint")}</span>
                    </div>
                    <Switch checked={brand.flow.show_financing} onChange={(v) => set({ flow: { ...brand.flow, show_financing: v } })} />
                  </div>
                  {OPTIONAL_STEPS.map((s) => (
                    <div className="switch-row" key={s.step}>
                      <div>
                        <b>{t(s.label)}</b>
                      </div>
                      <Switch checked={brand.flow.steps.includes(s.step)} onChange={(v) => toggleStep(s.step, v)} />
                    </div>
                  ))}
                </div>
              </Card>
              <CharterMarkCard checked={!!brand.show_charter_mark} onChange={(v) => set({ show_charter_mark: v })} />
            </>
          )}
          {tab === "proposal" && (
            <>
              <ProposalSection value={brand.proposal ?? {}} ours={ours} error={stepsError} disabled={!canManage} onChange={(v) => set({ proposal: v })} />
              <CompanySignatureCard
                value={brand.proposal?.company_signer}
                disabled={!canManage}
                showErrors
                onChange={(company_signer) => set({ proposal: { ...(brand.proposal ?? {}), company_signer } })}
              />
              <Card title={t("branding.pages.title")} description={t("branding.pages.description")}>
                <div className="switch-row">
                  <div>
                    <b>{t("branding.pages.wiring.title")}</b>
                    <span className="hint">
                      {t("branding.pages.wiring.hint")}
                    </span>
                  </div>
                  <Switch
                    checked={!!brand.proposal?.show_wiring_diagram}
                    onChange={(v) => set({ proposal: { ...(brand.proposal ?? {}), show_wiring_diagram: v } })}
                  />
                </div>
              </Card>
            </>
          )}
          <div className="spread">
            <span className="hint">{dirty ? t("branding.save.unsaved") : t("branding.save.allSaved")}</span>
            {saveBar}
          </div>
        </div>

        <div className="sticky">
          {tab === "proposal" ? (
            <Card title={t("branding.where.title")} description={t("branding.where.description")}>
              <ul className="brand-where small">
                {(["cover", "about", "next", "acceptance", "system", "wiring"] as const).map((id) => (
                  <li key={id}>{intl.formatMessage({ id: `branding.where.${id}` }, { b: (c) => <b>{c}</b> })}</li>
                ))}
              </ul>
            </Card>
          ) : (
            <CalculatorPreview orgId={orgId} brand={brand} />
          )}
        </div>
      </div>
    </>
  );
}
